import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity,
  ArrowDown,
  ArrowUp,
  Clock,
  Crown,
  Medal,
  Minus,
  Radio,
  Rocket,
  Sparkles,
  Target,
  Trophy,
  Users,
  Zap,
} from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { TeamAvatar } from '../components/team/Avatar';
import { TEAM_QUESTION_COUNT } from '../lib/arena-rules';
import { formatFaNumber } from '../lib/format';
import { averageAnswerMs, formatDuration, teamPerformanceLabel } from '../lib/team-performance';
import type { ArenaEvent, TeamPublic } from '../types/arena';

const statusLabel: Record<ArenaEvent['status'], string> = {
  registration: 'ثبت‌نام',
  countdown: 'آماده شروع',
  running: 'زنده',
  paused: 'مکث',
  finished: 'پایان‌یافته',
};

export function LeaderboardPage({ event, teams }: { event: ArenaEvent; teams: TeamPublic[] }) {
  const leaderboardCleared = event.leaderboardVisible === false;
  const visibleTeams = useMemo(() => (leaderboardCleared ? [] : teams), [leaderboardCleared, teams]);
  const previousRanks = useRef(new Map<string, number>());
  const currentRanks = useMemo(() => new Map(visibleTeams.map((team, index) => [team.id, index + 1])), [visibleTeams]);
  const isFinal = event.status === 'finished';
  const updatedAt = new Date().toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const [first, second, third] = visibleTeams;
  const topThree = [first, second, third].filter(Boolean) as TeamPublic[];
  const totalSubmitted = visibleTeams.reduce((sum, team) => sum + team.submittedCount, 0);
  const totalCorrect = visibleTeams.reduce((sum, team) => sum + team.correctCount, 0);
  const averageAccuracy = Math.round((totalCorrect / Math.max(1, totalSubmitted)) * 100);
  const answeredTeams = visibleTeams.filter((team) => team.submittedCount > 0);
  const fastestTeam = [...answeredTeams].sort((a, b) => averageAnswerMs(a) - averageAnswerMs(b))[0];
  const averageTime = answeredTeams.length
    ? answeredTeams.reduce((sum, team) => sum + averageAnswerMs(team), 0) / answeredTeams.length
    : 0;

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      previousRanks.current = currentRanks;
    }, 900);
    return () => window.clearTimeout(timeout);
  }, [currentRanks]);

  return (
    <main
      className="dbx-aurora arena-grid relative min-h-screen overflow-x-hidden bg-[#050711] px-3 py-3 text-white sm:px-5 lg:px-8 lg:py-6"
      dir="rtl"
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -right-28 top-12 size-72 rounded-full bg-brand/20 blur-[90px]" />
        <div className="absolute left-4 top-20 size-80 rounded-full bg-cyan/14 blur-[100px]" />
        <div className="absolute bottom-0 left-1/3 size-96 rounded-full bg-gold/12 blur-[120px]" />
      </div>

      <section className="relative mx-auto flex w-full max-w-7xl flex-col gap-4 lg:gap-5">
        <PublicHeader event={event} updatedAt={updatedAt} />

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px] xl:grid-cols-[minmax(0,1fr)_380px]">
          <div className="min-w-0 space-y-4">
            <HeroPanel teamsCount={visibleTeams.length} averageAccuracy={averageAccuracy} isFinal={isFinal} leaderboardCleared={leaderboardCleared} />

            <div className="grid gap-3 md:grid-cols-3">
              {topThree.map((team, index) => (
                <PodiumCard
                  key={team.id}
                  team={team}
                  rank={visibleTeams.findIndex((item) => item.id === team.id) + 1 || index + 1}
                  featured={team.id === first?.id}
                />
              ))}
              {!topThree.length && <EmptyPodium leaderboardCleared={leaderboardCleared} />}
            </div>

            <section className="overflow-hidden rounded-[28px] border border-white/12 bg-white/[.075] shadow-[0_22px_90px_rgba(0,0,0,.28)] backdrop-blur-2xl">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-4 sm:px-5">
                <div>
                  <h2 className="text-xl font-black">جدول زنده تیم‌ها</h2>
                  <p className="mt-1 text-xs text-white/45">
                    رتبه‌ها بر اساس امتیاز Query، درستی پاسخ، سرعت و کیفیت نسبی پاسخ‌ها به‌روزرسانی می‌شوند.
                  </p>
                </div>
                <span className="inline-flex items-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-400/10 px-3 py-2 text-xs font-black text-emerald-200">
                  <Radio className="size-4" />
                  پخش عمومی زنده
                </span>
              </div>

              <motion.div layout className="divide-y divide-white/8">
                <AnimatePresence initial={false}>
                  {visibleTeams.map((team, index) => {
                    const rank = index + 1;
                    const oldRank = previousRanks.current.get(team.id) ?? team.previousRank ?? rank;
                    const delta = oldRank - rank;
                    return <LeaderboardRow key={team.id} team={team} rank={rank} delta={delta} />;
                  })}
                </AnimatePresence>
              </motion.div>

              {!visibleTeams.length && (
                <div className="grid min-h-[260px] place-items-center px-6 text-center text-white/50">
                  <div>
                    <Sparkles className="mx-auto mb-4 size-10 text-brand" />
                    {leaderboardCleared
                      ? 'فعلاً داده‌ای برای نمایش عمومی وجود ندارد. با شروع مسابقه بعدی، تابلو دوباره زنده می‌شود.'
                      : 'هنوز تیمی وارد مسابقه نشده؛ به محض ورود اولین تیم، این تابلو زنده می‌شود.'}
                  </div>
                </div>
              )}
            </section>
          </div>

          <aside className="space-y-4">
            <LiveStatsPanel
              teams={visibleTeams}
              totalCorrect={totalCorrect}
              averageAccuracy={averageAccuracy}
              fastestTeam={fastestTeam}
              averageTime={averageTime}
              isFinal={isFinal}
            />
          </aside>
        </div>
      </section>
    </main>
  );
}

function PublicHeader({ event, updatedAt }: { event: ArenaEvent; updatedAt: string }) {
  return (
    <header className="rounded-[30px] border border-white/12 bg-[#0C1324]/85 p-4 shadow-[0_22px_80px_rgba(0,0,0,.32)] backdrop-blur-xl">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <span className="grid size-16 shrink-0 place-items-center rounded-[24px] border border-white/15 bg-white/95 p-2 shadow-[0_0_40px_rgba(242,106,33,.22)]">
            <img src="/mainLogo.webp" alt="DataBInex" className="size-full object-contain" />
          </span>
          <div className="min-w-0">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand/30 bg-brand/12 px-3 py-1 text-xs font-black text-brand">
              <Trophy className="size-3.5" />
              DataBInex Arena
            </span>
            <h1 className="mt-2 truncate text-2xl font-black tracking-tight sm:text-3xl">
              {displayEventName(event.name)}
            </h1>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          <StatusBadge status={event.status} />
          <span className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[.06] px-3 py-2 text-xs font-bold text-white/55">
            <Clock className="size-4 text-white/45" />
            آخرین به‌روزرسانی: {updatedAt}
          </span>
        </div>
      </div>
    </header>
  );
}

function HeroPanel({
  teamsCount,
  averageAccuracy,
  isFinal,
  leaderboardCleared,
}: {
  teamsCount: number;
  averageAccuracy: number;
  isFinal: boolean;
  leaderboardCleared: boolean;
}) {
  return (
    <section className="relative overflow-hidden rounded-[34px] border border-white/12 bg-white/[.075] p-5 shadow-[0_26px_100px_rgba(0,0,0,.30)] backdrop-blur-2xl sm:p-6">
      <div className="absolute -left-16 -top-16 size-52 rounded-full bg-cyan/14 blur-[70px]" />
      <div className="absolute -bottom-20 right-20 size-64 rounded-full bg-brand/16 blur-[85px]" />
      <div className="relative grid gap-5 lg:grid-cols-[1fr_280px] lg:items-end">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-gold/25 bg-gold/10 px-3 py-1.5 text-xs font-black text-gold">
            <Sparkles className="size-4" />
            {leaderboardCleared ? 'تابلوی عمومی خالی' : isFinal ? 'نتیجه نهایی مسابقه' : 'تابلوی عمومی زنده'}
          </span>
          <h2 className="mt-4 text-3xl font-black leading-tight sm:text-4xl lg:text-5xl">
            {leaderboardCleared ? 'فعلاً چیزی برای نمایش نیست' : 'رقابت Queryها همین‌جا دیده می‌شود'}
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-7 text-white/60 sm:text-base">
            {leaderboardCleared
              ? 'تابلوی عمومی توسط مدیر پاک شده است. اطلاعات مسابقه در پنل مدیر و آرشیوها حفظ می‌شود.'
              : 'هر تیم با نوشتن Query کامل T-SQL امتیاز می‌گیرد؛ این صفحه برای نمایش عمومی رتبه‌ها در سالن مسابقه آماده است.'}
          </p>
        </div>

        <div className="grid grid-cols-3 gap-2 rounded-[24px] border border-white/10 bg-[#080D1A]/70 p-3">
          <MiniStat icon={<Users className="size-5" />} label="تیم‌ها" value={formatFaNumber(teamsCount)} />
          <MiniStat icon={<Target className="size-5" />} label="دقت" value={`${formatFaNumber(averageAccuracy)}٪`} />
          <MiniStat icon={<Zap className="size-5" />} label="سؤال" value={formatFaNumber(TEAM_QUESTION_COUNT)} />
        </div>
      </div>
    </section>
  );
}

function PodiumCard({ team, rank, featured = false }: { team: TeamPublic; rank: number; featured?: boolean }) {
  const accuracy = Math.round((team.correctCount / Math.max(1, team.submittedCount)) * 100);
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative overflow-hidden rounded-[30px] border p-4 shadow-[0_18px_70px_rgba(0,0,0,.24)] backdrop-blur-2xl sm:p-5 ${
        featured
          ? 'border-gold/55 bg-gradient-to-b from-gold/18 to-white/[.08]'
          : 'border-white/12 bg-white/[.07]'
      }`}
    >
      <div className="absolute -left-10 -top-10 size-28 rounded-full bg-brand/16 blur-3xl" />
      <div className="relative flex items-center justify-between gap-3">
        <RankMedal rank={rank} />
        <span className="rounded-full border border-white/10 bg-white/[.07] px-3 py-1 text-xs font-black text-white/55">
          {teamPerformanceLabel(team)}
        </span>
      </div>
      <div className="relative mt-5 flex items-center gap-4">
        <TeamAvatar avatarId={team.avatarId} color={team.color} className="size-16 rounded-[22px] text-xl" />
        <div className="min-w-0">
          <h3 className="truncate text-xl font-black" dir="auto">{team.name}</h3>
          <p className="mt-1 text-xs text-white/45">رتبه {formatFaNumber(rank)}</p>
        </div>
      </div>
      <div className="relative mt-5 grid grid-cols-3 gap-2 text-center">
        <ScoreChip label="امتیاز" value={formatFaNumber(team.score)} />
        <ScoreChip label="درست" value={`${formatFaNumber(team.correctCount)}/${formatFaNumber(TEAM_QUESTION_COUNT)}`} />
        <ScoreChip label="دقت" value={`${formatFaNumber(accuracy)}٪`} />
      </div>
    </motion.article>
  );
}

function LeaderboardRow({ team, rank, delta }: { team: TeamPublic; rank: number; delta: number }) {
  const accuracy = Math.round((team.correctCount / Math.max(1, team.submittedCount)) * 100);
  const averageMs = averageAnswerMs(team);
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ layout: { type: 'spring', stiffness: 360, damping: 32 } }}
      className={`grid gap-3 px-4 py-4 sm:px-5 lg:grid-cols-[70px_minmax(0,1.25fr)_120px_130px_125px_92px] lg:items-center ${
        rank === 1 ? 'bg-gold/10' : 'bg-transparent'
      }`}
    >
      <div className="flex items-center justify-between gap-3 lg:block">
        <RankMedal rank={rank} compact />
        <RankDelta delta={delta} className="lg:hidden" />
      </div>

      <div className="flex min-w-0 items-center gap-3">
        <TeamAvatar avatarId={team.avatarId} color={team.color} className="size-12 rounded-[16px]" />
        <div className="min-w-0">
          <b className="block truncate text-base font-black" dir="auto">{team.name}</b>
          <span className="mt-1 block text-xs text-white/42">{teamPerformanceLabel(team)}</span>
        </div>
      </div>

      <StatCell label="امتیاز" value={formatFaNumber(team.score)} strong />
      <StatCell label="حل‌شده" value={`${formatFaNumber(team.correctCount)} / ${formatFaNumber(TEAM_QUESTION_COUNT)}`} />
      <StatCell label="میانگین زمان" value={team.submittedCount ? formatDuration(averageMs) : '—'} />

      <div className="flex items-center justify-between gap-3 lg:block">
        <div className="flex items-center gap-2">
          <span className="relative grid size-9 place-items-center rounded-full bg-white/10">
            <span
              className="absolute inset-0 rounded-full"
              style={{ background: `conic-gradient(#22C55E ${accuracy}%, rgba(255,255,255,.12) 0)` }}
            />
            <i className="relative size-6 rounded-full bg-[#0B1020]" />
          </span>
          <b className="text-sm">{formatFaNumber(accuracy)}٪</b>
        </div>
        <RankDelta delta={delta} className="mt-2 hidden lg:flex" />
      </div>
    </motion.div>
  );
}

function LiveStatsPanel({
  teams,
  totalCorrect,
  averageAccuracy,
  fastestTeam,
  averageTime,
  isFinal,
}: {
  teams: TeamPublic[];
  totalCorrect: number;
  averageAccuracy: number;
  fastestTeam: TeamPublic | undefined;
  averageTime: number;
  isFinal: boolean;
}) {
  const solvedTeams = teams.filter((team) => team.correctCount > 0);
  return (
    <section className="rounded-[30px] border border-white/12 bg-white/[.075] p-5 shadow-[0_22px_80px_rgba(0,0,0,.24)] backdrop-blur-2xl">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-black">نبض مسابقه</h2>
        <span className="grid size-11 place-items-center rounded-2xl bg-brand/15 text-brand">
          <Activity className="size-5" />
        </span>
      </div>
      <div className="mt-5 grid gap-3">
        <InsightCard icon={<Users />} label="تیم‌های حاضر" value={formatFaNumber(teams.length)} detail="ظرفیت نهایی: ۱۰ تیم" tone="blue" />
        <InsightCard icon={<Target />} label="پاسخ‌های درست" value={formatFaNumber(totalCorrect)} detail={`${formatFaNumber(solvedTeams.length)} تیم حداقل یک پاسخ درست دارند`} tone="green" />
        <InsightCard icon={<Medal />} label="میانگین دقت" value={`${formatFaNumber(averageAccuracy)}٪`} detail="بر اساس پاسخ‌های ارسال‌شده" tone="gold" />
        <InsightCard icon={<Rocket />} label={isFinal ? 'سریع‌ترین تیم' : 'فعلاً سریع‌ترین'} value={fastestTeam?.name ?? '—'} detail={fastestTeam ? formatDuration(averageAnswerMs(fastestTeam)) : 'هنوز پاسخی ثبت نشده'} tone="orange" />
        <InsightCard icon={<Clock />} label="میانگین زمان پاسخ" value={averageTime ? formatDuration(averageTime) : '—'} detail="میانگین تیم‌هایی که پاسخ داده‌اند" tone="purple" />
      </div>
    </section>
  );
}

function MiniStat({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[.055] p-3">
      <span className="mx-auto grid size-10 place-items-center rounded-xl bg-brand/12 text-brand">{icon}</span>
      <b className="mt-2 block text-center text-xl font-black">{value}</b>
      <span className="mt-0.5 block text-center text-[11px] text-white/42">{label}</span>
    </div>
  );
}

function ScoreChip({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#080D1A]/45 px-2 py-3">
      <b className="block text-lg font-black">{value}</b>
      <span className="mt-1 block text-[10px] text-white/42">{label}</span>
    </div>
  );
}

function StatCell({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/[.045] px-3 py-2 lg:block lg:border-0 lg:bg-transparent lg:p-0">
      <span className="text-[11px] text-white/42 lg:block">{label}</span>
      <b className={`text-sm ${strong ? 'text-xl text-brand' : 'text-white/85'}`}>{value}</b>
    </div>
  );
}

function RankMedal({ rank, compact = false }: { rank: number; compact?: boolean }) {
  const isTop = rank <= 3;
  const tone = rank === 1 ? 'bg-gold text-[#201400]' : rank === 2 ? 'bg-slate-200 text-slate-700' : rank === 3 ? 'bg-orange-300 text-[#2A1200]' : 'border-white/12 bg-white/[.06] text-white/70';
  return (
    <span className={`inline-grid place-items-center rounded-2xl font-black ${compact ? 'size-11' : 'size-12'} ${tone}`}>
      {isTop ? <Crown className={`${compact ? 'size-5' : 'size-6'} ${rank === 1 ? 'fill-[#201400]' : ''}`} /> : formatFaNumber(rank)}
    </span>
  );
}

function RankDelta({ delta, className = '' }: { delta: number; className?: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-black ${
        delta > 0 ? 'text-emerald-300' : delta < 0 ? 'text-red-300' : 'text-white/35'
      } ${className}`}
    >
      {delta > 0 ? <ArrowUp className="size-4" /> : delta < 0 ? <ArrowDown className="size-4" /> : <Minus className="size-4" />}
      {delta ? formatFaNumber(Math.abs(delta)) : 'بدون تغییر'}
    </span>
  );
}

function InsightCard({
  icon,
  label,
  value,
  detail,
  tone,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  detail: string;
  tone: 'blue' | 'green' | 'gold' | 'orange' | 'purple';
}) {
  const tones = {
    blue: 'text-cyan bg-cyan/10 border-cyan/20',
    green: 'text-emerald-300 bg-emerald-400/10 border-emerald-400/20',
    gold: 'text-gold bg-gold/10 border-gold/20',
    orange: 'text-brand bg-brand/10 border-brand/20',
    purple: 'text-purple-300 bg-purple-400/10 border-purple-400/20',
  } as const;
  return (
    <div className="rounded-[22px] border border-white/10 bg-white/[.045] p-4">
      <div className="flex items-center gap-3">
        <span className={`grid size-11 shrink-0 place-items-center rounded-2xl border ${tones[tone]}`}>{icon}</span>
        <div className="min-w-0">
          <span className="block text-xs font-bold text-white/42">{label}</span>
          <strong className="mt-1 block truncate text-lg font-black">{value}</strong>
        </div>
      </div>
      <span className="mt-3 block text-[11px] leading-5 text-white/38">{detail}</span>
    </div>
  );
}

function StatusBadge({ status }: { status: ArenaEvent['status'] }) {
  const isLive = status === 'running' || status === 'countdown';
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-2xl border px-3 py-2 text-xs font-black ${
        isLive
          ? 'border-emerald-400/25 bg-emerald-400/12 text-emerald-200'
          : status === 'finished'
            ? 'border-gold/25 bg-gold/12 text-gold'
            : 'border-white/10 bg-white/[.06] text-white/55'
      }`}
    >
      <span className={`size-2.5 rounded-full ${isLive ? 'bg-emerald-400 shadow-[0_0_16px_rgba(52,211,153,.8)]' : 'bg-white/35'}`} />
      {statusLabel[status]}
    </span>
  );
}

function EmptyPodium({ leaderboardCleared }: { leaderboardCleared: boolean }) {
  return (
    <div className="md:col-span-3 rounded-[30px] border border-white/12 bg-white/[.055] p-8 text-center text-white/50">
      <Trophy className="mx-auto mb-4 size-10 text-gold" />
      {leaderboardCleared ? 'تابلوی عمومی فعلاً خالی است.' : 'جایگاه برترها هنوز خالی است.'}
    </div>
  );
}

function displayEventName(name: string | undefined) {
  if (!name || /DataBinex\s+T-SQL\s+Arena/i.test(name)) return 'DataBInex Arena';
  return name.replace(/T-SQL\s+Arena/gi, 'Arena');
}
